import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Upload, X, FileText, Calendar, Building2, Trash2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useMedicalProviders } from '@/lib/firebase/queries/medicalProviders';
import { toast } from 'sonner';
import type { ProgressReport } from '@/types';

export interface PendingProgressReport {
  file: File | null;
  providerId: string;
  providerName: string;
  providerNameAr: string;
  reportDate: string;              // yyyy-MM-dd
  department: string;
  notes: string;
  thumbnailUrl?: string;
}

interface ProgressReportUploaderProps {
  /** Already-persisted reports (rendered read-only in the list). */
  reports: ProgressReport[];
  /** Newly-staged reports awaiting Firestore/Storage commit by the parent. */
  pending: PendingProgressReport[];
  onPendingChange: (pending: PendingProgressReport[]) => void;
  /** Called when the user deletes an already-persisted report. */
  onDeleteExisting?: (index: number) => void;
  translationPrefix: string;       // e.g. "common.progressReports"
  readOnly?: boolean;
  /** Optional warning banner text ("last-report" warning). Rendered above the list. */
  lastReportWarning?: string;
  /** Render only the add trigger + modal; the parent owns the lists. */
  hideList?: boolean;
}

export function ProgressReportUploader({
  reports,
  pending,
  onPendingChange,
  onDeleteExisting,
  translationPrefix,
  readOnly = false,
  lastReportWarning,
  hideList = false,
}: ProgressReportUploaderProps) {
  const { t, dir, language } = useLanguage();
  const { providers } = useMedicalProviders();
  const activeProviders = providers.filter(p => p.isActive);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState<number | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    providerId: '',
    reportDate: new Date().toISOString().split('T')[0],
    department: '',
    notes: '',
  });

  const resetForm = () => setFormData({
    providerId: '',
    reportDate: new Date().toISOString().split('T')[0],
    department: '',
    notes: '',
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error(t('common.fileSizeError'));
      return;
    }
    setSelectedFile(file);
    if (file.type.startsWith('image/') || file.type === 'application/pdf') {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }
    resetForm();
    setIsModalOpen(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAdd = () => {
    if (!selectedFile) {
      toast.error(t(`${translationPrefix}.validation.fileRequired`));
      return;
    }
    if (!formData.providerId || !formData.reportDate) {
      toast.error(t(`${translationPrefix}.validation.required`));
      return;
    }
    const provider = activeProviders.find(p => p.id === formData.providerId);
    const entry: PendingProgressReport = {
      file: selectedFile,
      providerId: formData.providerId,
      providerName: provider?.name || '',
      providerNameAr: provider?.nameAr || '',
      reportDate: formData.reportDate,
      department: formData.department,
      notes: formData.notes,
      thumbnailUrl: previewUrl || undefined,
    };
    onPendingChange([...pending, entry]);
    setIsModalOpen(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    resetForm();
  };

  const handleRemovePending = (idx: number) => {
    onPendingChange(pending.filter((_, i) => i !== idx));
  };

  return (
    <div dir={dir}>
      {!readOnly && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            className="hidden"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              className="gap-2"
            >
              <Upload className="h-4 w-4" />
              {t(`${translationPrefix}.addButton`)}
            </Button>
          </div>
        </>
      )}

      {lastReportWarning && (
        <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          {lastReportWarning}
        </div>
      )}

      {/* Persisted list */}
      {!hideList && reports.length > 0 && (
        <ul className="mt-3 space-y-2">
          {reports.map((r, idx) => (
            <li key={r.url + idx} className="flex items-center gap-3 border rounded-lg p-2">
              <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <a href={r.url} target="_blank" rel="noreferrer" className="text-sm font-medium truncate underline">
                  {r.name}
                </a>
                <div className="flex flex-wrap gap-2 mt-1">
                  {r.providerName && (
                    <Badge variant="secondary" className="gap-1">
                      <Building2 className="h-3 w-3" />
                      {language === 'ar' ? (r as any).providerNameAr || r.providerName : r.providerName}
                    </Badge>
                  )}
                  {r.reportDate && (
                    <Badge variant="outline" className="gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(r.reportDate).toISOString().split('T')[0]}
                    </Badge>
                  )}
                </div>
                {r.notes && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.notes}</p>}
              </div>
              {!readOnly && onDeleteExisting && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setConfirmDeleteIdx(idx)}
                  aria-label={t(`${translationPrefix}.delete.confirmTitle`)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Staged (pending) list */}
      {!hideList && pending.length > 0 && (
        <ul className="mt-3 space-y-2">
          {pending.map((p, idx) => (
            <li key={idx} className="flex items-center gap-3 border rounded-lg p-2 border-dashed">
              <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{p.file?.name}</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  <Badge variant="secondary">
                    {language === 'ar' ? p.providerNameAr || p.providerName : p.providerName}
                  </Badge>
                  <Badge variant="outline">{p.reportDate}</Badge>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => handleRemovePending(idx)}
                aria-label={t('common.remove')}
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {!hideList && reports.length === 0 && pending.length === 0 && (
        <p className="mt-3 text-sm text-muted-foreground">
          {t(`${translationPrefix}.list.empty`)}
        </p>
      )}

      {/* Add modal */}
      <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) { setIsModalOpen(false); setSelectedFile(null); setPreviewUrl(null); resetForm(); } }}>
        <DialogContent className="max-w-3xl" dir={dir}>
          <DialogHeader dir={dir}>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {t(`${translationPrefix}.modal.title`)}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label>{t(`${translationPrefix}.modal.provider`)}</Label>
              <Select value={formData.providerId} onValueChange={(v) => setFormData(prev => ({ ...prev, providerId: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder={t(`${translationPrefix}.modal.providerPlaceholder`)} />
                </SelectTrigger>
                <SelectContent>
                  {activeProviders.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {language === 'ar' ? p.nameAr || p.name : p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t(`${translationPrefix}.modal.reportDate`)}</Label>
                <Input
                  type="date"
                  value={formData.reportDate}
                  onChange={e => setFormData(prev => ({ ...prev, reportDate: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t(`${translationPrefix}.modal.department`)}</Label>
                <Input
                  value={formData.department}
                  onChange={e => setFormData(prev => ({ ...prev, department: e.target.value }))}
                  placeholder={t(`${translationPrefix}.modal.departmentPlaceholder`)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t(`${translationPrefix}.modal.notes`)}</Label>
              <Textarea
                value={formData.notes}
                onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
              />
            </div>
            {previewUrl && (
              <div className="border rounded-lg h-64 overflow-hidden bg-muted/30">
                {selectedFile?.type === 'application/pdf'
                  ? <iframe src={previewUrl} className="w-full h-full" title="Report preview" />
                  : <img src={previewUrl} alt="preview" className="max-w-full max-h-full mx-auto object-contain" />}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleAdd}>{t('common.add')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={confirmDeleteIdx !== null} onOpenChange={(open) => { if (!open) setConfirmDeleteIdx(null); }}>
        <AlertDialogContent dir={dir}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t(`${translationPrefix}.delete.confirmTitle`)}</AlertDialogTitle>
            <AlertDialogDescription>
              {reports.length === 1
                ? t(`${translationPrefix}.delete.lastReportWarning`)
                : t(`${translationPrefix}.delete.description`)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDeleteIdx !== null) onDeleteExisting?.(confirmDeleteIdx);
                setConfirmDeleteIdx(null);
              }}
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
